/**
 * Unified abstraction over the two supported input shapes (dropped ZIP or
 * dropped folder). All downstream code consumes a FileBundle regardless of
 * where the files originated.
 *
 * A FileBundle exposes:
 *   - the files' relative paths (normalised: forward slashes, no leading './')
 *   - a helper to read an individual file as text (full file content; used
 *     for info.json)
 *   - a helper to open a streaming text iterator over a file (used for CSVs
 *     so we never materialise a 150 MB string)
 */

export interface FileHandle {
  /** Path relative to the bundle root. */
  path: string;
  /** Size in bytes if known. */
  size?: number;
  /** Name of the file only (basename). */
  name: string;
}

export interface FileBundle {
  /** Files under this bundle, excluding directories. */
  readonly files: readonly FileHandle[];
  /** Read a file as a UTF-8 string. */
  readText(path: string): Promise<string>;
  /**
   * Stream a file's UTF-8 text content in chunks. The iterator must be fully
   * consumed or disposed; callers typically pass the return value directly
   * to `parseCsvStream`.
   */
  streamText(path: string): AsyncIterable<string>;
}

/**
 * Given an iterable of full relative paths, find the single directory that
 * contains `info.json` plus at least one `*.csv` file. Used to tolerate
 * nested wrapper folders inside a ZIP or dropped folder.
 */
export function findDataRoot(paths: readonly string[]): string {
  const infoPaths = paths.filter((p) => basename(p) === "info.json");
  if (infoPaths.length === 0) {
    throw new Error("No info.json found in the uploaded bundle.");
  }
  // Prefer the deepest info.json that also has a sibling CSV.
  infoPaths.sort((a, b) => b.split("/").length - a.split("/").length);
  for (const ip of infoPaths) {
    const dir = dirname(ip);
    const prefix = dir === "" ? "" : dir + "/";
    const hasCsv = paths.some(
      (p) =>
        p.startsWith(prefix) &&
        p.slice(prefix.length).indexOf("/") === -1 &&
        p.toLowerCase().endsWith(".csv"),
    );
    if (hasCsv) return dir;
  }
  throw new Error(
    "info.json found but no sibling CSV files were present next to it.",
  );
}

export function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? path : path.slice(i + 1);
}

export function dirname(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? "" : path.slice(0, i);
}

/**
 * Return a new FileBundle whose `files` and `read*` methods are scoped to
 * the given subdirectory. The scoped bundle reports paths relative to
 * `dataRoot`.
 */
export function scopeBundle(bundle: FileBundle, dataRoot: string): FileBundle {
  const prefix = dataRoot === "" ? "" : dataRoot + "/";
  const scopedFiles: FileHandle[] = bundle.files
    .filter((f) => f.path.startsWith(prefix))
    .filter((f) => f.path.slice(prefix.length).indexOf("/") === -1)
    .map((f) => ({ ...f, path: f.path.slice(prefix.length) }));
  return {
    files: scopedFiles,
    readText: (path) => bundle.readText(prefix + path),
    streamText: (path) => bundle.streamText(prefix + path),
  };
}

/** Normalise path separators and strip any leading "./" for consistency. */
export function normalisePath(path: string): string {
  let out = path.replace(/\\/g, "/");
  if (out.startsWith("./")) out = out.slice(2);
  if (out.startsWith("/")) out = out.slice(1);
  return out;
}
